import { forwardRef, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { parseQuickAdd, type QuickAddField } from '@horizon/shared';
import { Button } from '@/components/ui/button';

export interface QuickAddProps {
  projectOptions?: { id: string; label: string }[];
  projectsLoading?: boolean;
  projectsError?: boolean;
  retryProjects?: () => void;
  today: string;
  pending?: boolean;
  onSubmit: (input: string, parse: boolean, projectId?: string | null) => void | Promise<unknown>;
}

const FIELD_LABEL: Record<QuickAddField, string> = {
  scheduledFor: 'quickAdd.field.scheduledFor',
  dueDate: 'quickAdd.field.dueDate',
  tag: 'quickAdd.field.tag',
  estimate: 'quickAdd.field.estimate',
};

/**
 * Единственное обязательное поле — название. Разбор дат и меток показывается
 * заранее и всегда обратим: кнопка оставляет строку целиком как текст
 * (docs/PRODUCT.md, принципы интерфейса).
 */
export const QuickAdd = forwardRef<HTMLInputElement, QuickAddProps>(function QuickAdd(
  { today, pending, onSubmit, projectOptions, projectsLoading, projectsError, retryProjects },
  ref,
) {
  const { t, i18n } = useTranslation();
  const [value, setValue] = useState('');
  const [parse, setParse] = useState(true);
  const [error, setError] = useState(false);
  const [projectId, setProjectId] = useState('');
  const saving = useRef(false);

  const preview = useMemo(
    () => (value.trim() && today ? parseQuickAdd(value, { today }) : null),
    [value, today],
  );
  const recognised = parse && preview ? preview.matches : [];

  const submit = async (withParse: boolean) => {
    const input = value.trim();
    if (!input || saving.current) return;
    saving.current = true;
    setError(false);
    try {
      await onSubmit(input, withParse, projectId || null);
      setValue('');
      setParse(true);
    } catch {
      setError(true);
    } finally {
      saving.current = false;
    }
  };

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        void submit(parse);
      }}
    >
      <div className="flex items-center gap-2 rounded-card border border-line bg-surface px-2 py-1.5 focus-within:border-accent">
        <input
          ref={ref}
          disabled={pending}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={t('quickAdd.placeholder')}
          aria-label={t('quickAdd.label')}
          enterKeyHint="done"
          autoComplete="off"
          className="min-h-11 min-w-0 flex-1 bg-transparent px-2 text-ink outline-none placeholder:text-muted"
        />
        <Button
          type="submit"
          size="icon"
          variant="primary"
          disabled={!value.trim() || pending}
          aria-label={t('quickAdd.submit')}
        >
          <Plus aria-hidden="true" className="size-4" />
        </Button>
      </div>

      {projectOptions && (
        <div className="flex flex-col gap-1">
          <label className="text-sm text-muted">
            {t('quickAdd.project')}
            <select
              className="mt-1 min-h-11 w-full rounded-md border border-line bg-surface px-3 text-ink"
              value={projectId}
              disabled={pending || projectsLoading}
              onChange={(e) => setProjectId(e.target.value)}
            >
              <option value="">{t('editor.noProject')}</option>
              {projectOptions.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.label}
                </option>
              ))}
            </select>
          </label>
          <p className="text-xs text-muted">{t('quickAdd.projectHint')}</p>
          {projectsLoading && <p role="status">{t('common.loading')}</p>}
          {projectsError && (
            <p role="alert">
              {t('errors.loadFailed')} <Button onClick={retryProjects}>{t('common.retry')}</Button>
            </p>
          )}
        </div>
      )}
      {error && (
        <p role="alert" className="text-sm text-muted">
          {t('polish.draftError')}
        </p>
      )}
      {recognised.length > 0 && preview && (
        <div className="flex flex-wrap items-center gap-2 px-1 text-xs">
          <span className="text-muted">{t('quickAdd.recognised')}</span>
          {recognised.map((m) => (
            <span
              key={`${m.field}-${m.text}`}
              className="rounded-md bg-accent-soft px-2 py-1 text-ink-2"
            >
              {t(FIELD_LABEL[m.field], { value: describe(m.field, preview, i18n.language) })}
            </span>
          ))}
          <Button
            size="sm"
            variant="ghost"
            className="text-muted underline"
            onClick={() => submit(false)}
          >
            {t('quickAdd.keepAsText')}
          </Button>
        </div>
      )}
    </form>
  );
});

function describe(
  field: QuickAddField,
  preview: ReturnType<typeof parseQuickAdd>,
  locale: string,
): string {
  switch (field) {
    case 'scheduledFor':
      return preview.scheduledFor ? formatDate(preview.scheduledFor, locale) : '';
    case 'dueDate':
      return preview.dueDate ? formatDate(preview.dueDate, locale) : '';
    case 'tag':
      return preview.tags.join(', ');
    case 'estimate':
      return String(preview.estimateMinutes ?? '');
  }
}

function formatDate(date: string, locale: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(
    new Date(Date.UTC(y, m - 1, d)),
  );
}
